import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const CertBadge = ({ label }) => (
  <div className="inline-flex items-center gap-2 bg-white/10 border border-gold-500/40 backdrop-blur-sm rounded-full px-4 py-1.5">
    <div className="w-1.5 h-1.5 bg-gold-500 rounded-full" />
    <span className="text-sm font-semibold text-white tracking-wide">{label}</span>
  </div>
);

const StatCard = ({ number, label }) => (
  <div className="text-center">
    <div className="text-3xl md:text-4xl font-black text-gold-500 font-heading">{number}</div>
    <div className="text-sm text-slate-400 mt-1 font-medium">{label}</div>
  </div>
);

const ServiceCard = ({ title, subtitle, description, href, icon }) => (
  <div className="group relative bg-navy-700 border border-navy-600 hover:border-gold-500/50 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-gold-500/10">
    <div className="w-14 h-14 bg-gold-500/10 border border-gold-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-gold-500/20 transition-colors">
      <span className="text-2xl">{icon}</span>
    </div>
    <h3 className="text-xl font-bold text-white font-heading mb-1">{title}</h3>
    <p className="text-gold-500 text-xs font-semibold uppercase tracking-wider mb-4">{subtitle}</p>
    <p className="text-slate-400 leading-relaxed mb-6 text-sm">{description}</p>
    <Link
      to={href}
      className="inline-flex items-center gap-2 text-gold-500 hover:text-gold-400 font-semibold text-sm group-hover:gap-3 transition-all"
    >
      Learn More
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    </Link>
  </div>
);

const DiffCard = ({ icon, title, body }) => (
  <div className="flex gap-4">
    <div className="w-12 h-12 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center shrink-0">
      <span className="text-xl">{icon}</span>
    </div>
    <div>
      <h4 className="font-bold text-navy-800 font-heading mb-1">{title}</h4>
      <p className="text-slate-500 text-sm leading-relaxed">{body}</p>
    </div>
  </div>
);

const AboutCard = ({ icon, title, body }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-gold-200 hover:shadow-md transition-all">
    <div className="text-3xl mb-3">{icon}</div>
    <h4 className="font-bold text-navy-800 font-heading text-sm mb-2">{title}</h4>
    <p className="text-slate-500 text-xs leading-relaxed">{body}</p>
  </div>
);

const Home = () => {
  return (
    <div className="pt-20">

      {/* Hero */}
      <section className="relative min-h-screen flex items-center bg-navy-800 overflow-hidden">
        <div className="absolute inset-0 opacity-100" style={GRID_BG} />
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gold-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-navy-950/60 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-3 mb-10">
              <CertBadge label="SBA 8(a) Certified" />
              <CertBadge label="EDWOSB" />
              <CertBadge label="100% Woman-Owned" />
              <CertBadge label="SAM Registered" />
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white font-heading leading-[1.05] mb-6">
              Delivering{' '}
              <span className="text-gold-500">Mission&#8209;Critical</span>{' '}
              Solutions for the Federal Government
            </h1>

            <p className="text-xl text-slate-300 max-w-2xl leading-relaxed mb-10">
              A 100% Woman-Owned, SBA 8(a) Certified small business headquartered in Ashburn, VA —
              providing expert Facilities Management and Information Technology services to federal agencies nationwide.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/services/facilities"
                className="bg-gold-500 hover:bg-gold-400 text-navy-900 px-8 py-4 rounded-xl font-bold text-base transition-colors shadow-lg shadow-gold-500/20"
              >
                Explore Our Services
              </Link>
              <Link
                to="/about/capability-statement"
                className="border border-white/30 hover:border-gold-500/60 text-white hover:text-gold-400 px-8 py-4 rounded-xl font-semibold text-base transition-all hover:bg-white/5"
              >
                Capability Statement
              </Link>
            </div>

            <div className="mt-20 pt-10 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-8">
              <StatCard number="8(a)" label="SBA Certified" />
              <StatCard number="2" label="Core Service Lines" />
              <StatCard number="10+" label="NAICS Codes" />
              <StatCard number="Ashburn" label="VA Headquarters" />
            </div>
          </div>
        </div>
      </section>

      {/* Certifications bar */}
      <section className="bg-white border-b border-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {[
              'SBA 8(a) Certified',
              'EDWOSB',
              'WOSB',
              'SAM.gov Registered',
              'Ashburn, VA',
            ].map((cert) => (
              <div key={cert} className="flex items-center gap-2.5 text-navy-800">
                <div className="w-1.5 h-1.5 bg-gold-500 rounded-full shrink-0" />
                <span className="font-semibold text-sm tracking-wide">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">About DTX</p>
              <h2 className="text-4xl md:text-5xl font-black text-navy-800 font-heading leading-tight mb-6">
                A Trusted Partner for Federal Agencies
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-5">
                DTX Digital Transformation, LLC is a certified Woman-Owned Small Business (WOSB) and SBA 8(a) awardee
                headquartered in Ashburn, Virginia. We deliver high-impact Facilities Management and Information
                Technology solutions that empower government clients to operate efficiently and securely.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our team brings deep federal contracting experience, combining technical expertise with a commitment
                to on-time, on-budget delivery. We understand the unique requirements of government procurement and
                hold all required certifications to compete across federal set-aside opportunities.
              </p>
              <Link
                to="/about/capability-statement"
                className="inline-flex items-center gap-2 text-gold-600 hover:text-gold-700 font-bold transition-colors"
              >
                View Our Capability Statement
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <AboutCard
                icon="🏆"
                title="SBA 8(a) Certified"
                body="Eligible for sole-source and set-aside contracts up to $4.5M (services) and $7M (manufacturing)."
              />
              <AboutCard
                icon="👩‍💼"
                title="100% Woman-Owned"
                body="EDWOSB and WOSB certified — qualifying for additional federal set-aside contract opportunities."
              />
              <AboutCard
                icon="🏛️"
                title="Federal Focus"
                body="Deep understanding of federal acquisition regulations, FAR/DFAR compliance, and agency reporting."
              />
              <AboutCard
                icon="📍"
                title="Ashburn, VA"
                body="Strategically located in the DC Metro Area, close to key federal clients and procurement hubs."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="bg-navy-800 py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-100" style={GRID_BG} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">What We Do</p>
            <h2 className="text-4xl md:text-5xl font-black text-white font-heading mb-4">Core Service Lines</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Two focused service areas, delivered with federal-grade precision and accountability.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <ServiceCard
              icon="🏢"
              title="Facilities Management"
              subtitle="Space Planning & Workplace Solutions"
              description="Comprehensive workplace solutions including space management, move coordination, signage, help desk support, and IWMS/CAFM/CMMS implementation for federal facilities."
              href="/services/facilities"
            />
            <ServiceCard
              icon="💻"
              title="Information Technology"
              subtitle="GRC, DevOps & Cybersecurity"
              description="Full-spectrum IT support covering project management, governance & compliance (FISMA, NIST), DevOps, and cybersecurity — built for the federal technology environment."
              href="/services/it"
            />
          </div>
        </div>
      </section>

      {/* Why DTX */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Why Choose DTX</p>
              <h2 className="text-4xl md:text-5xl font-black text-navy-800 font-heading leading-tight mb-6">
                Built for Government.<br />Trusted by Agencies.
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We combine the agility of a small business with the discipline and compliance rigor of a seasoned
                federal contractor. Our certifications open doors — our performance keeps them open.
              </p>
            </div>
            <div className="space-y-8">
              <DiffCard
                icon="✅"
                title="Certified & Compliant"
                body="SBA 8(a), EDWOSB, and WOSB certifications create streamlined procurement pathways for contracting officers seeking set-aside opportunities."
              />
              <DiffCard
                icon="⚡"
                title="Small Business Agility"
                body="Direct access to decision-makers, faster response times, and flexible engagement models that large contractors can't match."
              />
              <DiffCard
                icon="🔒"
                title="Security-First Approach"
                body="All IT engagements are designed to adhere to FISMA, NIST 800-53, and CMMC standards as applicable to agency requirements."
              />
              <DiffCard
                icon="📊"
                title="Results-Driven Delivery"
                body="We measure success by mission outcomes — on-time delivery, budget adherence, and stakeholder satisfaction at every engagement."
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-800 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-100" style={GRID_BG} />
        <div className="absolute top-1/2 left-1/2 w-[800px] h-[400px] bg-gold-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white font-heading mb-4">
            Ready to Partner with DTX?
          </h2>
          <p className="text-slate-300 text-lg mb-10 max-w-xl mx-auto">
            Whether you're a contracting officer exploring set-aside options or an agency seeking a capable small
            business partner — we'd love to connect.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-gold-500 hover:bg-gold-400 text-navy-900 px-8 py-4 rounded-xl font-bold transition-colors shadow-lg shadow-gold-500/20"
            >
              Contact Us Today
            </Link>
            <Link
              to="/about/naics-codes"
              className="border border-white/30 hover:border-gold-500/60 text-white hover:text-gold-400 px-8 py-4 rounded-xl font-semibold transition-all hover:bg-white/5"
            >
              View NAICS Codes
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
